import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, ViewEncapsulation, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { AlertService } from 'app/mock-api/common/services/alert.service';
import { Subject, takeUntil } from 'rxjs';
import { DatePipe } from '@angular/common';
import { FormattingPipe } from 'app/modules/shared/services/formatting-data.pipe';
import * as XLSX from 'xlsx';
import { CopyOnDblclickDirective } from '@fuse/directives/copy-on-dblclick.directive';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { TruncatePipe } from 'app/core/pipes/truncate.pipe';

@Component({
  selector: 'app-report-data-table-list',
  standalone: true,
  imports: [
    CommonModule,
    CdkScrollable,
    TranslocoModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FormattingPipe,
    CopyOnDblclickDirective,
    ClipboardModule,
    TruncatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './report-data-table-list.component.html',
  styleUrl: './report-data-table-list.component.scss',
  providers: [DatePipe, DecimalPipe],
  encapsulation: ViewEncapsulation.None,
})
export class ReportDataTableListComponent implements OnInit, AfterViewInit, OnDestroy {
  protected _unsubscribeAll = new Subject<void>();
  lang: string = 'en';

  currentIndex: number = 0;
  /** Normalized slides: each has `data` as row arrays; optional metadata from API. */
  queries: Array<{
    title?: string;
    tableName?: string;
    query?: string;
    data: any[];
  }> = [];
  name: string = '';
  expandedQueryIndices = new Set<number>();
  /** Preview length before "see full query". Sized for the 96vw dialog (was 150 at 70%/90vw). */
  readonly queryPreviewLimit = 300;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _dialogRef: MatDialogRef<ReportDataTableListComponent>,
    private _dialog: MatDialog,
    private _alertService: AlertService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _translocoService: TranslocoService,
    private datePipe: DatePipe
  ) { }

  ngOnInit() {
    this._translocoService.langChanges$.pipe(takeUntil(this._unsubscribeAll)).subscribe((res) => {
      this.lang = res;
      this._changeDetectorRef.markForCheck();
    });

    this.queries = this.buildNormalizedQueries();
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Maps every supported API/dialog payload into the same shape: an array of
   * `{ title?, tableName?, query?, data: rows[] }`.
   */
  private buildNormalizedQueries(): Array<{
    title?: string;
    tableName?: string;
    query?: string;
    data: any[];
  }> {
    const dt = this.data?.dataTable;
    if (dt == null) {
      return [];
    }

    if (typeof dt === 'object' && !Array.isArray(dt) && Array.isArray((dt as { queries?: unknown }).queries)) {
      return ((dt as { queries: any[] }).queries).map(q => ({
        title: q?.title,
        tableName: q?.tableName,
        query: q?.query,
        data: Array.isArray(q?.data) ? q.data : []
      }));
    }

    if (!Array.isArray(dt) || dt.length === 0) {
      return [];
    }

    const first = dt[0];

    if (Array.isArray(first)) {
      return (dt as any[][]).map(rows => ({
        title: this.data.chartTitle,
        tableName: this.data.tableName,
        data: Array.isArray(rows) ? rows : []
      }));
    }

    if (first && typeof first === 'object' && 'data' in first && Array.isArray((first as { data?: unknown }).data)) {
      return (dt as any[]).map(q => ({
        title: q?.title,
        tableName: q?.tableName,
        query: q?.query,
        data: Array.isArray(q?.data) ? q.data : []
      }));
    }

    return [{
      title: this.data.chartTitle,
      tableName: this.data.tableName,
      data: dt as any[]
    }];
  }

  ngAfterViewInit() {
    this._changeDetectorRef.markForCheck();
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      --this.currentIndex;
      this._changeDetectorRef.markForCheck();
    }
  }

  nextSlide() {
    if (this.currentIndex < this.queries.length - 1) {
      ++this.currentIndex;
      this._changeDetectorRef.markForCheck();
    }
  }

  toggleQueryExpanded(index: number): void {
    if (this.expandedQueryIndices.has(index)) {
      this.expandedQueryIndices.delete(index);
    } else {
      this.expandedQueryIndices.add(index);
    }
    this.expandedQueryIndices = new Set(this.expandedQueryIndices);
    this._changeDetectorRef.markForCheck();
  }

  isQueryExpanded(index: number): boolean {
    return this.expandedQueryIndices.has(index);
  }

  isQueryLong(query: string): boolean {
    return Boolean(query) && query.length > this.queryPreviewLimit;
  }

  getKeysForQuery(query: { data?: any[] }): string[] {
    const rows = query?.data;
    return Array.isArray(rows) && rows.length > 0 ? Object.keys(rows[0]) : [];
  }

  /** Uses `query.title` when set; otherwise translated "Data Table". */
  slideHeading(query: { title?: string }): string {
    const raw = query.title;
    if (raw != null) {
      const s = String(raw).trim();
      if (s.length > 0 && s.toLowerCase() !== 'null') {
        return s;
      }
    }
    return this._translocoService.translate('Data Table');
  }

  exportAsCsv() {
    const workbook = XLSX.utils.book_new();
    const tablesToExport = this.getAllTablesForExport();
    const usedSheetNames = new Set<string>();

    if (!tablesToExport.length) {
      return;
    }

    tablesToExport.forEach((table, index) => {
      if (!Array.isArray(table.rows) || !table.rows.length) {
        return;
      }

      const formattedRows = table.rows.map((row: any) => {
        const formattedRow: Record<string, any> = {};
        Object.keys(row || {}).forEach((key) => {
          formattedRow[this.formatExportHeader(key)] = row[key];
        });
        return formattedRow;
      });

      const worksheet = XLSX.utils.json_to_sheet(formattedRows);
      const sheetName = this.getUniqueSheetName(table.name, index, usedSheetNames);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    if (!workbook.SheetNames.length) {
      return;
    }

    const fileName = this.getExportFileName();
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  }

  /**
   * Formats a header for export: removes hyphens/dashes and converts to title case.
   * e.g. "first-name" -> "First Name", "some_key" -> "Some Key"
   */
  private formatExportHeader(header: string): string {
    const withSpaces = header.replace(/[-_]+/g, ' ').trim();
    return withSpaces
      .toLowerCase()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private generateCsvContent(data: any[]): string {
    const rawKeys = Object.keys(data[0]);
    const headers = rawKeys.map(key => this.formatExportHeader(key)).join(',');
    const rows = data
      .map(row =>
        Object.values(row)
          .map(value =>
            typeof value === 'string' && value.includes(',') ? `"${value}"` : value
          )
          .join(',')
      )
      .join('\n');

    return `${headers}\n${rows}`; // Combine headers and rows
  }

  private getAllTablesForExport(): Array<{ name?: string; rows: any[] }> {
    if (!this.queries?.length) {
      return [];
    }
    return this.queries
      .map((q, index) => ({
        name: q?.title || q?.tableName || this.data?.tableName || this.data?.name || `Table ${index + 1}`,
        rows: Array.isArray(q?.data) ? q.data : []
      }))
      .filter(table => table.rows.length > 0);
  }

  private getSheetName(name: string | undefined, index: number): string {
    const fallback = `Table ${index + 1}`;
    const rawName = (name || fallback).toString().trim();
    const cleaned = rawName.replace(/[\\/*?:[\]]/g, '').replace(/^'+|'+$/g, '');
    return (cleaned || fallback).slice(0, 31);
  }

  private getUniqueSheetName(
    baseLabel: string | undefined,
    index: number,
    usedNames: Set<string>
  ): string {
    const base = this.getSheetName(baseLabel, index);
    let candidate = base;
    let suffix = 2;

    while (usedNames.has(candidate)) {
      const suffixText = ` (${suffix})`;
      const maxBaseLength = Math.max(0, 31 - suffixText.length);
      candidate = `${base.slice(0, maxBaseLength)}${suffixText}`;
      suffix++;
    }

    usedNames.add(candidate);
    return candidate;
  }

  /** Safe segment for Windows filenames (export_{agent}_{id}.xlsx). */
  private sanitizeFileNameSegment(segment: string): string {
    const s = (segment ?? '').toString().trim();
    const cleaned = s.replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, '_');
    return cleaned || 'export';
  }

  private getExportFileName(): string {
    const messagePayload = this.data?.dataTable;
    const messageId =
      this.data?.messageId ??
      messagePayload?.id ??
      this.data?.id;
    const agentRaw =
      this.data?.exportAgentName ??
      messagePayload?.senderType ??
      (this.data.from === 'chat' ? 'agent' : null) ??
      this.data?.name ??
      'export';
    const agentSegment = this.sanitizeFileNameSegment(String(agentRaw));

    const useChatStyleExportName =
      this.data.from === 'chat' ||
      this.data.messageId != null ||
      this.data.exportAgentName != null;

    if (
      useChatStyleExportName &&
      messageId != null &&
      messageId !== ''
    ) {
      return `export_${agentSegment}_${messageId}`;
    }

    const firstTableName = this.getAllTablesForExport().find(table => !!table?.name)?.name;
    const rawName =
      this.data.tableName || firstTableName || this.data.chartTitle || this.data.name || 'tableName';
    return this.sanitizeFileNameSegment(rawName.toString().trim()) || 'tableName';
  }


  shouldShowSortIcons(key: string, slideIndex: number): boolean {
    const rows = this.queries?.[slideIndex]?.data;
    if (!Array.isArray(rows) || rows.length === 0) {
      return false;
    }
    const sampleValue = rows[0][key];
    return !isNaN(parseFloat(sampleValue)) && isFinite(sampleValue);
  }

  sortData(key: string, order: 'asc' | 'desc') {
    const compareFn = (a: any, b: any) => {
      const valueA = a[key];
      const valueB = b[key];

      if (valueA == null || valueB == null) {
        return 0;
      }

      if (order === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    };

    const q = this.queries[this.currentIndex];
    if (q && Array.isArray(q.data)) {
      q.data = [...q.data].sort(compareFn);
    }

    this._changeDetectorRef.markForCheck();
  }

  isArrayOfArrays(arr: any[]): boolean {
    return arr.length > 0 && arr.every(item => Array.isArray(item) || item === null);
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();

    this._dialogRef.close();
  }
}
